import { useCallback, useState } from "react";
import Icon from "../icons/Icon";
import Modal from "../modals/Modal";
import { useToast } from "../../contexts/ToastContext";
import { setEmployeePinFn } from "../../services/kioskCallables";
import "../../styles/components/settings-pages.css";
import "../../styles/components/kiosk-page.css";

// Trivially-guessable PINs the server rejects. functions/src/kiosk/pinHelpers.js
// WEAK_PINS is the SOURCE OF TRUTH; this mirror exists only so we never GENERATE
// a PIN the server would reject. weakPinParity.test.js fails if the two drift.
const WEAK = new Set([
  "0000", "1111", "2222", "3333", "4444", "5555", "6666", "7777", "8888", "9999",
  "1234", "2345", "3456", "4567", "5678", "6789", "0123",
  "4321", "5432", "6543", "7654", "8765", "9876", "3210",
  "1212", "2121", "1122", "1313", "6969", "2580", "0852", "1357", "2468",
]);

const randomPin = () => {
  let pin;
  do {
    pin = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
  } while (WEAK.has(pin));
  return pin;
};

const spaced = (pin) => pin.split("").join(" ");

/**
 * ResetKioskPinModal — admin set/reset of an employee's kiosk PIN. Generates a
 * fresh non-weak PIN to share with the employee; setEmployeePin clears any
 * lockout. The employee can change it themselves at the kiosk afterwards.
 */
const ResetKioskPinModal = ({ employeeId, employeeName, onClose }) => {
  const toast = useToast();
  const [pin, setPin] = useState(randomPin);
  const [busy, setBusy] = useState(false);

  const regenerate = useCallback(() => setPin(randomPin()), []);

  const setIt = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await setEmployeePinFn({ employeeId, pin });
      toast.success(`PIN set for ${employeeName || employeeId}.`);
      onClose();
    } catch (err) {
      toast.error(err?.message || "Failed to set PIN.");
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} busy={busy} labelledBy="kiosk-pin-title" className="kiosk-modal">
        <div className="kiosk-modal-header">
          <h2 id="kiosk-pin-title" className="kiosk-modal-title">Reset kiosk PIN</h2>
          <div className="kiosk-modal-sub">
            For <strong>{employeeName || "this employee"}{employeeId ? ` · ${employeeId}` : ""}</strong>.
          </div>
        </div>
        <div className="kiosk-modal-body">
          <div className="kiosk-field">
            <label className="kiosk-label">New 4-digit PIN</label>
            <div className="kiosk-pin-display">{spaced(pin)}</div>
            <div className="kiosk-hint">
              Share this with the employee. They can change it themselves at the kiosk.{" "}
              <button
                type="button"
                onClick={regenerate}
                disabled={busy}
                style={{ background: "none", border: "none", color: "var(--ui-primary)", cursor: "pointer", padding: 0, font: "inherit" }}
              >
                Generate another
              </button>
            </div>
          </div>
          <div className="kiosk-note info">
            <Icon name="info" />
            <div>Resetting clears any lockout from failed attempts.</div>
          </div>
        </div>
        <div className="kiosk-modal-footer">
          <button type="button" className="settings-btn settings-btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="settings-btn settings-btn-primary" onClick={setIt} disabled={busy}>
            {busy ? "Setting…" : "Set PIN"}
          </button>
        </div>
    </Modal>
  );
};

export default ResetKioskPinModal;
